import { useMemo } from 'react'

// Generic types for grouping functionality
export type GroupingOption = string

export interface GroupingConfig<T> {
  /** Function to extract group keys from an item */
  getGroupKeys: (item: T, groupBy: GroupingOption) => string[]
  /** Label for the "none" grouping option */
  noneGroupLabel?: string
  /** Plural and singular labels for items */
  itemLabels?: {
    singular: string
    plural: string
  }
}

export interface GroupingResult<T> {
  [groupName: string]: T[]
}

// Generic hook for grouping items
export function useGrouping<T>(
  items: T[],
  groupBy: GroupingOption,
  config: GroupingConfig<T>
): GroupingResult<T> {
  return useMemo(() => {
    if (groupBy === 'none') {
      const noneLabel = config.noneGroupLabel || `All ${config.itemLabels?.plural || 'Items'}`
      return { [noneLabel]: items }
    }

    const groups: Record<string, T[]> = {}

    items.forEach((item) => {
      const groupKeys = config.getGroupKeys(item, groupBy)

      // Add item to each relevant group
      groupKeys.forEach((key) => {
        if (!groups[key]) {
          groups[key] = []
        }
        groups[key].push(item)
      })
    })

    // Sort groups by key name
    return Object.keys(groups)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = groups[key]
          return acc
        },
        {} as Record<string, T[]>
      )
  }, [items, groupBy, config])
}
